import React, { useState, useEffect } from "react";
import { Grid, Typography, TextField, Box, FormControl, InputLabel, Select, MenuItem, CircularProgress, Button } from "@material-ui/core";
import axios from "axios";
import SummaryTable from "./SummaryTable.js";
import { BACKEND_BASE_URL } from "../../config.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { Pagination } from "@mui/material";

export default function Summary() {
  const [data, setData] = useState([]); // Data fetched from the backend
  const [searchQuery, setSearchQuery] = useState(""); // Search query
  const [sortKey, setSortKey] = useState(""); // Default sorting by date (recent first)
  const [filteredData, setFilteredData] = useState([]); // Data to display
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    // Fetch existing data from the backend when the component loads
    setLoading(true)
    axios
      .get(`${BACKEND_BASE_URL}/api/urls/?page=${currentPage}&page_size=${itemsPerPage}`)
      .then((response) => {
        const fetchedData = response.data.results;
        setTotalCount(response.data.count);
       

        setData(fetchedData); // Set the sorted data
        setFilteredData(fetchedData); // Also set it to the filtered data
      })
      .catch((error) => console.error("Error fetching data:", error)).finally(() => {
        setLoading(false);
      });
  }, [currentPage]);

  const handleSortChange = (event) => {
    const key = event.target.value;
    setSortKey(key);

    const sortedData = [...filteredData].sort((a, b) => {
      if (key === "entry_date_desc") {
        // Sort by ID (recent first)
        return b.id - a.id;
      } else if (key === "entry_date_asc") {
        // Sort by ID (oldest first)
        return a.id - b.id;
      } else if (key === "alphabetical_asc") {
        // Sort alphabetically A-Z
        const aValue = `${a.state || ""} ${a.county || ""} ${a.municipality_name || ""}`.toLowerCase();
        const bValue = `${b.state || ""} ${b.county || ""} ${b.municipality_name || ""}`.toLowerCase();
        return aValue.localeCompare(bValue);
      } else if (key === "alphabetical_desc") {
        // Sort alphabetically Z-A
        const aValue = `${a.state || ""} ${a.county || ""} ${a.municipality_name || ""}`.toLowerCase();
        const bValue = `${b.state || ""} ${b.county || ""} ${b.municipality_name || ""}`.toLowerCase();
        return bValue.localeCompare(aValue);
      }
      return 0; // Default
    });

    setFilteredData(sortedData);
  };


  

  const handleSearchChange = (event) => {
    const query = event.target.value.toLowerCase(); // Normalize query

    setSearchQuery(query);

   
  };
  const handleSearch = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_BASE_URL}/api/urls/`, {
        params: {
          page: 1,
          page_size: itemsPerPage,
          search: searchQuery,  // Use the state here
        },
      });

      setData(response.data.results);
      setFilteredData(response.data.results);
      setTotalCount(response.data.count);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error searching:", error);
    } finally {
      setLoading(false);
    }
  };

  // Stale-while-revalidate: always show layout; show previous data until new data arrives
  const isInitialLoad = loading && filteredData.length === 0;

  return (
    <>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h4" style={{ fontWeight: "bold" }}>
              Transcript Summary
            </Typography>
            <Box display="flex" alignItems="center">
              <FormControl variant="outlined" size="small" style={{ marginRight: "10px", minWidth: "150px" }}>
                <InputLabel>Sort By</InputLabel>
                <Select value={sortKey} onChange={handleSortChange} label="Sort By">
                  <MenuItem value="entry_date_desc">Latest Entry</MenuItem>
                  <MenuItem value="entry_date_asc">Oldest Entry</MenuItem>
                  <MenuItem value="alphabetical_asc">A-Z (State, County, Town)</MenuItem>
                  <MenuItem value="alphabetical_desc">Z-A (State, County, Town)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                placeholder="Search..."
                variant="outlined"
                size="small"
                value={searchQuery}
                InputProps={{
                  startAdornment: (
                    <FontAwesomeIcon
                      icon={faMagnifyingGlass}
                      style={{ marginRight: "8px", color: "#555" }}
                    />
                  ),
                }}
                onChange={handleSearchChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                style={{ marginRight: "10px" }}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                style={{
                  marginRight: "10px",
                  backgroundColor: "#0B194A",
                  color: "white",
                }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} style={{ color: "white" }} /> : "Search"}
              </Button>

            </Box>

          </Box>
        </Grid>

        <Grid item xs={12} style={{ position: "relative" }}>
          {isInitialLoad ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : (
            <>
              {loading && (
                <Box position="absolute" top={8} right={16} zIndex={1}>
                  <CircularProgress size={24} />
                </Box>
              )}
              <SummaryTable data={filteredData} />
            </>
          )}
        </Grid>

        <Box display="flex" justifyContent="center" alignItems={"center"} margin={"auto"} mb={2}>
          <Pagination
            count={Math.ceil(totalCount / itemsPerPage)}
            page={currentPage}
            onChange={(e, value) => setCurrentPage(value)}
            color="primary"
            disabled={loading}
          />
        </Box>
      </Grid>
    </>
  );
}
